// --- Settings Panel: Admin CRUD for auction settings ---
import React, { useState, useEffect } from 'react';
import { Settings, Save, RotateCcw, DollarSign, Timer, TrendingUp, Wallet, Users } from 'lucide-react';
import type { AuctionSettings, AppUser } from '../types';
import { DEFAULT_AUCTION_SETTINGS } from '../hooks/useFirestoreData';
import { formatCurrency } from '../utils/helpers';

interface SettingsPanelProps {
  auctionSettings: AuctionSettings | null;
  currentUser: AppUser;
  onSave: (settings: Partial<AuctionSettings>, currentUser: AppUser) => Promise<boolean>;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ auctionSettings, currentUser, onSave }) => {
  const [minBid, setMinBid] = useState(DEFAULT_AUCTION_SETTINGS.minBidPoints);
  const [maxBid, setMaxBid] = useState(DEFAULT_AUCTION_SETTINGS.maxBidPoints);
  const [bidIncrement, setBidIncrement] = useState(DEFAULT_AUCTION_SETTINGS.bidIncrement);
  const [defaultBudget, setDefaultBudget] = useState(DEFAULT_AUCTION_SETTINGS.defaultTeamBudget);
  const [timerDuration, setTimerDuration] = useState(DEFAULT_AUCTION_SETTINGS.timerDuration);
  const [requiredMale, setRequiredMale] = useState(DEFAULT_AUCTION_SETTINGS.requiredMalePlayers || 0);
  const [requiredFemale, setRequiredFemale] = useState(DEFAULT_AUCTION_SETTINGS.requiredFemalePlayers || 0);

  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load current settings when available
  useEffect(() => {
    if (auctionSettings) {
      setMinBid(auctionSettings.minBidPoints);
      setMaxBid(auctionSettings.maxBidPoints);
      setBidIncrement(auctionSettings.bidIncrement);
      setDefaultBudget(auctionSettings.defaultTeamBudget);
      setTimerDuration(auctionSettings.timerDuration);
      setRequiredMale(auctionSettings.requiredMalePlayers || 0);
      setRequiredFemale(auctionSettings.requiredFemalePlayers || 0);
    }
  }, [auctionSettings]);

  // Check for changes
  useEffect(() => {
    const current = auctionSettings || DEFAULT_AUCTION_SETTINGS;
    const changed = 
      minBid !== current.minBidPoints ||
      maxBid !== current.maxBidPoints ||
      bidIncrement !== current.bidIncrement ||
      defaultBudget !== current.defaultTeamBudget ||
      timerDuration !== current.timerDuration ||
      requiredMale !== (current.requiredMalePlayers || 0) ||
      requiredFemale !== (current.requiredFemalePlayers || 0);
    setHasChanges(changed);
  }, [minBid, maxBid, bidIncrement, defaultBudget, timerDuration, requiredMale, requiredFemale, auctionSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        minBidPoints: minBid,
        maxBidPoints: maxBid,
        bidIncrement,
        defaultTeamBudget: defaultBudget,
        timerDuration,
        requiredMalePlayers: requiredMale,
        requiredFemalePlayers: requiredFemale,
      }, currentUser);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMinBid(DEFAULT_AUCTION_SETTINGS.minBidPoints);
    setMaxBid(DEFAULT_AUCTION_SETTINGS.maxBidPoints);
    setBidIncrement(DEFAULT_AUCTION_SETTINGS.bidIncrement);
    setDefaultBudget(DEFAULT_AUCTION_SETTINGS.defaultTeamBudget);
    setTimerDuration(DEFAULT_AUCTION_SETTINGS.timerDuration);
    setRequiredMale(DEFAULT_AUCTION_SETTINGS.requiredMalePlayers || 0);
    setRequiredFemale(DEFAULT_AUCTION_SETTINGS.requiredFemalePlayers || 0);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-white" />
          <h2 className="text-xl font-bold text-white">Auction Settings</h2>
        </div>
        <p className="text-white/70 text-sm mt-1">Configure bidding parameters for the auction</p>
      </div>

      {/* Settings Form */}
      <div className="p-6 space-y-6">
        {/* Bid Points Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Min Bid */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <DollarSign className="w-4 h-4 text-green-600" />
              Minimum Bid (pts)
            </label>
            <input
              type="number"
              value={minBid}
              onChange={e => setMinBid(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={1}
            />
            <p className="text-xs text-slate-500">Base price for all players</p>
          </div>

          {/* Max Bid */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <TrendingUp className="w-4 h-4 text-red-600" />
              Maximum Bid (pts)
            </label>
            <input
              type="number"
              value={maxBid}
              onChange={e => setMaxBid(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={minBid}
            />
            <p className="text-xs text-slate-500">Maximum allowed bid</p>
          </div>

          {/* Bid Increment */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Bid Increment (pts)
            </label>
            <input
              type="number"
              value={bidIncrement}
              onChange={e => setBidIncrement(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={1}
            />
            <p className="text-xs text-slate-500">Amount added per bid</p>
          </div>
        </div>

        {/* Budget & Timer Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Default Team Budget */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Wallet className="w-4 h-4 text-emerald-600" />
              Default Team Budget (pts)
            </label>
            <input
              type="number"
              value={defaultBudget}
              onChange={e => setDefaultBudget(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={100}
            />
            <p className="text-xs text-slate-500">Starting budget for new teams</p>
          </div>

          {/* Timer Duration */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Timer className="w-4 h-4 text-orange-600" />
              Timer Duration (seconds)
            </label>
            <input
              type="number"
              value={timerDuration}
              onChange={e => setTimerDuration(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={5}
              max={120}
            />
            <p className="text-xs text-slate-500">Countdown duration per player</p>
          </div>
        </div>

        {/* Squad Composition Limits Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2">
              <Users className="w-4 h-4" /> Squad Limits (0 = No Limit)
            </h4>
            <p className="text-xs text-slate-500">Enforce maximum number of players per team by gender</p>
          </div>

          {/* Max Male Players */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              Max Male Players
            </label>
            <input
              type="number"
              value={requiredMale}
              onChange={e => setRequiredMale(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={0}
            />
          </div>

          {/* Max Female Players */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              Max Female Players
            </label>
            <input
              type="number"
              value={requiredFemale}
              onChange={e => setRequiredFemale(Number(e.target.value))}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              min={0}
            />
          </div>
        </div>

        {/* Current Values Summary */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Current Configuration</h4>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-xs text-slate-500">Min Bid</p>
              <p className="font-bold text-green-600">{formatCurrency(minBid)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Max Bid</p>
              <p className="font-bold text-red-600">{formatCurrency(maxBid)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Increment</p>
              <p className="font-bold text-indigo-600">+{formatCurrency(bidIncrement)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Team Budget</p>
              <p className="font-bold text-emerald-600">{formatCurrency(defaultBudget)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Timer</p>
              <p className="font-bold text-orange-600">{timerDuration}s</p>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        {auctionSettings && (
          <p className="text-xs text-slate-400 text-center">
            Last updated by {auctionSettings.updatedByName} on{' '}
            {new Date(auctionSettings.updatedAt).toLocaleString()}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-colors ${
              hasChanges
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
